/**
 * Support tickets: the reporter's side (CLAUDE.md 12.3).
 *
 * The admin queue lives in `api/admin.ts`; the two share the attachment and message
 * shapes because the thread is the same thread seen from two chairs.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

export type TicketType = 'issue' | 'feature' | 'question'
export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent'

export interface TicketAttachment {
  id: number
  message_id: number | null
  uploader_id: number | null
  filename: string
  /** The type the server sniffed from the bytes, never the browser's claim. */
  media_type: string
  byte_size: number
  created_at: string
}

export interface TicketMessage {
  id: number
  author_id: number | null
  author_name: string | null
  body: string
  is_staff: boolean
  created_at: string
  attachments: TicketAttachment[]
}

export interface Ticket {
  id: number
  type: TicketType
  title: string
  status: TicketStatus
  priority: TicketPriority
  module_id: string | null
  awaiting_staff: boolean
  last_activity_at: string
  created_at: string
  updated_at: string | null
  message_count: number
  attachment_count: number
}

export interface TicketDetail extends Ticket {
  body: string
  position_snapshot: Record<string, unknown> | null
  attachments: TicketAttachment[]
  messages: TicketMessage[]
  can_reply: boolean
}

export interface TicketLimits {
  max_attachment_bytes: number
  allowed_media_types: string[]
  max_attachments_per_message: number
  max_attachments_per_ticket: number
}

export type TicketListFilter = 'active' | 'resolved' | 'closed' | 'all'

export const supportKeys = {
  all: ['tickets'] as const,
  list: (filter: TicketListFilter) => ['tickets', 'list', filter] as const,
  detail: (id: number) => ['tickets', 'detail', id] as const,
  limits: ['tickets', 'limits'] as const,
}

export function useTickets(filter: TicketListFilter) {
  const query = filter === 'all' ? '' : `?status=${filter}`
  return useQuery({
    queryKey: supportKeys.list(filter),
    queryFn: () => api.get<Ticket[]>(`/tickets${query}`),
  })
}

export function useTicket(id: number | null) {
  return useQuery({
    queryKey: supportKeys.detail(id ?? 0),
    queryFn: () => api.get<TicketDetail>(`/tickets/${id}`),
    enabled: id !== null && Number.isFinite(id),
  })
}

export function useTicketLimits() {
  return useQuery({
    queryKey: supportKeys.limits,
    queryFn: () => api.get<TicketLimits>('/tickets/limits'),
    staleTime: 5 * 60 * 1000,
  })
}

export interface TicketCreateInput {
  type: TicketType
  title: string
  body: string
  position_id?: number
}

export function useCreateTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TicketCreateInput) => api.post<TicketDetail>('/tickets', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: supportKeys.all }),
  })
}

export function useReplyToTicket(ticketId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      api.post<TicketMessage>(`/tickets/${ticketId}/messages`, { body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: supportKeys.all }),
  })
}

export function useRemoveAttachment(ticketId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (attachmentId: number) =>
      api.delete<void>(`/tickets/${ticketId}/attachments/${attachmentId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: supportKeys.all }),
  })
}

/**
 * Upload files against a ticket, or against one message on it.
 *
 * `admin` switches to the server-administration route, which authorises by system
 * role rather than by workspace membership.
 */
export function uploadAttachments(
  ticketId: number,
  files: File[],
  options: { messageId?: number | null; admin?: boolean } = {},
): Promise<TicketAttachment[]> {
  const form = new FormData()
  for (const file of files) form.append('files', file, file.name)
  const base = options.admin ? '/admin/tickets' : '/tickets'
  const query = options.messageId ? `?message_id=${options.messageId}` : ''
  return api.upload<TicketAttachment[]>(`${base}/${ticketId}/attachments${query}`, form)
}

/** The path the preview and download helpers fetch, with the bearer token. */
export function attachmentFilePath(
  ticketId: number,
  attachmentId: number,
  options: { admin?: boolean; inline?: boolean } = {},
): string {
  const base = options.admin ? '/api/v1/admin/tickets' : '/api/v1/tickets'
  return `${base}/${ticketId}/attachments/${attachmentId}/file${options.inline ? '?inline=true' : ''}`
}

/** A file the browser can preview: an image or a PDF. Everything else downloads. */
export function isPreviewable(attachment: Pick<TicketAttachment, 'media_type'>): boolean {
  return attachment.media_type.startsWith('image/') || attachment.media_type === 'application/pdf'
}
